import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import type { ScheduleRecord } from '~/services/OffersService';

import AppOnlyFlashEventCard from './AppOnlyFlashEventCard';
import { CalendarDay } from './appOnlyFlashCalendar';

interface Props {
  day: CalendarDay;
  isToday: boolean;
  isPast: boolean;
  schedules: Array<ScheduleRecord>;
  /** Most deals the region can hold per day, as svc-offer reports it. */
  maxPerDay: number;
  offersById: Map<string, App.Offer>;
  loadingOfferIds: Set<string>;
  onAddDeal: () => void;
  onMoveDeal: (schedule: ScheduleRecord) => void;
}

export default function AppOnlyFlashCalendarDay({
  day,
  isToday,
  isPast,
  schedules,
  maxPerDay,
  offersById,
  loadingOfferIds,
  onAddDeal,
  onMoveDeal,
}: Props) {
  // Up to the daily limit per region (a 48 hour deal counts on both its days), and past slots can't be created.
  const canAddDeal = !isPast && schedules.length < maxPerDay;

  return (
    <Box
      sx={{
        minHeight: 132,
        p: 0.75,
        borderRight: 1,
        borderBottom: 1,
        borderColor: 'divider',
        '&:nth-of-type(7n)': { borderRight: 0 },
        bgcolor: isPast ? 'grey.100' : day.inMonth ? 'background.paper' : 'grey.50',
        minWidth: 0,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Box
          sx={{
            width: 25,
            height: 25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            bgcolor: isToday ? 'primary.main' : 'transparent',
          }}
        >
          <Typography
            variant="caption"
            fontWeight={isToday ? 500 : 400}
            color={isToday ? 'primary.contrastText' : day.inMonth ? 'text.secondary' : 'text.disabled'}
          >
            {day.dayOfMonth}
          </Typography>
        </Box>
        {canAddDeal && (
          <Tooltip title="Add deal">
            <IconButton size="small" aria-label={`Add deal on ${day.date}`} onClick={onAddDeal}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      {schedules.map((schedule) => (
        <AppOnlyFlashEventCard
          key={schedule.id}
          schedule={schedule}
          offer={offersById.get(schedule.fk_offer_salesforce_id)}
          isOfferLoading={loadingOfferIds.has(schedule.fk_offer_salesforce_id)}
          onMove={onMoveDeal}
        />
      ))}
    </Box>
  );
}
